import { useQuery, ApolloError } from "@apollo/client";
import { createContext, useEffect, useReducer, useState } from "react";
import useLocalStorageState from "use-local-storage-state";
import {
  GET_PAGED_PERSONS_QUERY,
  GET_PERSONER_IDS_AND_EMAIL,
} from "../api/person/queries";
import { defaultPersonColumns } from "../config/person";
import { PagedPersonContext } from "../interfaces/pagedContext";
import {
  QueryFilter,
  PersonFilterKeys,
  FilterPreset,
} from "../interfaces/filters";
import { queryFilterReducer } from "./reducers/queryFilterReducer";
import { filterPresetsReducer } from "./reducers/savedFiltersReducer";
import { buildFilter } from "../util/queryBuilder";
import { getActiveColumnFields } from "../util/table";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
  FilterPresetsContext,
  FilterPresetsEventContext,
} from "./FiltersContext";
import { useTableSelection } from "../components/Table/useTableSelection";
import useActiveColumns from "../hooks/useActiveColumns";
import {
  IPagedPersonsQuery,
  IPagedPersonsResponse,
  PagedPersonsQueryField,
} from "../interfaces/personer";
import { IColumn, SortBy } from "../interfaces/table";
import {
  personIndexActiveColumnsKey,
  personIndexSortOrderKey,
} from "../util/localStorageKeys";
import { PersonDto } from "../interfaces/schema";
import { get, union } from "lodash";
import { DateFilterSetting } from "@/components/filter/date/types";
import { format } from "date-fns";

export const PagedPersonQueryContext = createContext<
  PagedPersonContext & { emailableIds: Array<string> }
>({
  loading: false,
  error: undefined,
  columns: [],
  refetch: undefined,
  setActiveColumns: () => {},
  setQuery: () => {},
  page: 1,
  pagedResponse: undefined,
  nextPage: undefined,
  prevPage: undefined,
  type: "person",
  emailableIds: [],
  selectAllError: undefined,
  clearSelectAllError: () => {},
  selection: {
    selectedIds: [],
    clearSelection: () => {},
    selectAll: () => {},
    addSelectedIds: () => {},
    removeSelectedIds: () => {},
    lastSelectedId: undefined,
    setLastSelectedId: () => {},
  },
});

export const PagedPersonQueryProvider = ({ children }) => {
  // type
  const type = "person";

  // state
  const [fetchAllSelected, setFetchAllSelected] = useState(false);
  const [selectAllTotalCount, setSelectAllTotalCount] = useState<number | null>(
    null,
  );
  const [selectAllError, setSelectAllError] = useState<ApolloError | undefined>(
    undefined,
  );
  const [query, setQuery] = useState<IPagedPersonsQuery>({
    page: 1,
  });
  const [emailableIds, setEmailableIds] = useState<Array<string>>([]);

  // localstorage state
  const [storedFilters, setStoredFilters] = useLocalStorageState<
    QueryFilter<PersonFilterKeys>[]
  >("personFilters", { defaultValue: [] });
  const [savedFiltersStorage, setSavedFiltersStorage] = useLocalStorageState<
    FilterPreset[]
  >("filterPresets", {
    defaultValue: [],
  });
  const [sortOrder] = useLocalStorageState<Array<SortBy>>(
    personIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  // reducers
  const [queryFilters, updateFilters] = useReducer(
    queryFilterReducer,
    storedFilters,
  );
  const [savedFilters, updateSavedFilters] = useReducer(
    filterPresetsReducer,
    savedFiltersStorage,
  );

  // business
  const { after, before } = query;
  const page = query.page ? query.page : 1;

  const {
    addSelectedIds,
    removeSelectedIds,
    selectedIds,
    clearSelection,
    selectAll,
    lastSelectedId,
    setLastSelectedId,
  } = useTableSelection();

  const [columns, setActiveColumns] = useActiveColumns(
    personIndexActiveColumnsKey,
    defaultPersonColumns,
  );

  const {
    data,
    error: selectAllQueryError,
    loading: fetchAllLoading,
  } = useQuery<IPagedPersonsResponse>(
    GET_PERSONER_IDS_AND_EMAIL(query, selectAllTotalCount ?? 0),
    {
      skip: !fetchAllSelected || !selectAllTotalCount,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  // effects
  useEffect(() => {
    setSavedFiltersStorage(savedFilters);
    setStoredFilters(queryFilters);
    setQuery({
      filters: buildFilter(queryFilters),
      order: {
        orderDirection: sortOrder[0].direction.toUpperCase() as OrderDirection,
        orderKey: sortOrder[0].field as PagedPersonsQueryField,
      },
    });
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryFilters,
    setQuery,
    setStoredFilters,
    setSavedFiltersStorage,
    savedFilters,
    sortOrder,
  ]);

  useEffect(() => {
    if (!fetchAllSelected || fetchAllLoading || !data) return;

    const selectedIds = data.personer.edges.map(
      (edge) => edge.node.id,
    ) as Array<string>;
    selectAll(selectedIds);

    const allEmailableIds = data.personer.edges.flatMap((edge) => {
      if (edge.node.prioriteretEmail) {
        return [edge.node.id as string];
      } else {
        return [];
      }
    });

    setEmailableIds(allEmailableIds);
    setFetchAllSelected(false);
    setSelectAllTotalCount(null);
  }, [data, fetchAllLoading, fetchAllSelected, selectAll]);

  useEffect(() => {
    if (selectAllQueryError && fetchAllSelected) {
      setSelectAllError(selectAllQueryError);
      setFetchAllSelected(false);
      setSelectAllTotalCount(null);
    }
  }, [selectAllQueryError, fetchAllSelected]);

  // effect to catch "wrongful" today filters
  useEffect(() => {
    const shouldCheckToday = storedFilters.some((filter) => {
      const dateFilterType: DateFilterSetting["filterType"] = get(
        filter,
        "settings.dateFilterSettings.filterType",
      );
      if (dateFilterType === "today") {
        return filter;
      }
    });

    if (shouldCheckToday) {
      storedFilters
        .filter((filter) => {
          const dateFilterType: DateFilterSetting["filterType"] = get(
            filter,
            "settings.dateFilterSettings.filterType",
          );
          if (dateFilterType === "today") {
            return filter;
          }
        })
        .forEach((filter) => {
          const nowDate = new Date();
          const dateFormat = "yyyy-MM-dd";

          const regex = /"\d\d\d\d-\d\d-\d\d"/gm;
          const filterString = `${filter.filter}`;
          const updatedDate = `"${format(nowDate, dateFormat)}"`;

          const updatedFilterString = filterString.replaceAll(
            regex,
            updatedDate,
          );
          filter.filter = updatedFilterString;
        });
    }
  }, [storedFilters]);

  // query
  const currentPage = usePagedPersonsQuery(
    {
      after: after,
      before: before,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order ?? undefined,
    },
    columns,
  );

  const nextPage = usePagedPersonsQuery(
    {
      after: currentPage.data?.personer.pageInfo.endCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order ?? undefined,
    },
    columns,
  );

  const prevPage = usePagedPersonsQuery(
    {
      before: currentPage.data?.personer.pageInfo.startCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order ?? undefined,
    },
    columns,
  );

  const loading = currentPage.loading || nextPage.loading || prevPage.loading;

  // wait? another effect?!
  useEffect(() => {
    if (selectedIds.length) {
      const currentPersoner = currentPage.data?.personer.edges.map(
        (edge) => edge.node as PersonDto,
      );

      // find selected current personer
      const selectedCurrentPersoner = currentPersoner?.flatMap((person) => {
        if (selectedIds.includes(person.id)) {
          return [person];
        } else {
          return [];
        }
      });

      // find selected emailable ids
      const selectedEmailableIds =
        selectedCurrentPersoner?.flatMap((person) => {
          if (person.prioriteretEmail) {
            return [person.id];
          } else {
            return [];
          }
        }) || [];

      setEmailableIds((latest) => {
        // filter all current ids out of the emailable list
        const filteredEmailableIds = latest.filter(
          (id) => !currentPersoner?.find((person) => person.id === id),
        );

        return union(filteredEmailableIds, selectedEmailableIds);
      });
    } else {
      setEmailableIds([]);
    }
  }, [currentPage.data?.personer.edges, selectedIds]);

  return (
    <PagedPersonQueryContext.Provider
      value={{
        loading,
        error: currentPage.error,
        pagedResponse: currentPage?.data,
        refetch: currentPage.refetch,
        columns,
        setActiveColumns,
        setQuery,
        query,
        page,
        nextPage,
        prevPage,
        type,
        emailableIds,
        selectAllError,
        clearSelectAllError: () => setSelectAllError(undefined),
        selection: {
          addSelectedIds,
          clearSelection,
          removeSelectedIds,
          selectAll: (totalCount: number) => {
            setSelectAllError(undefined);
            setSelectAllTotalCount(totalCount);
            setFetchAllSelected(true);
          },
          selectedIds,
          lastSelectedId,
          setLastSelectedId,
        },
      }}
    >
      <FilterPresetsContext.Provider value={savedFilters}>
        <FilterPresetsEventContext.Provider value={updateSavedFilters}>
          <PersonFiltersContext.Provider value={queryFilters}>
            <PersonFiltersEventContext.Provider value={updateFilters}>
              {children}
            </PersonFiltersEventContext.Provider>
          </PersonFiltersContext.Provider>
        </FilterPresetsEventContext.Provider>
      </FilterPresetsContext.Provider>
    </PagedPersonQueryContext.Provider>
  );
};

function usePagedPersonsQuery(
  query: IPagedPersonsQuery,
  columns: IColumn<PagedPersonsQueryField>[],
) {
  return useQuery<IPagedPersonsResponse>(
    GET_PAGED_PERSONS_QUERY({
      after: query.after,
      before: query.before,
      fields: getActiveColumnFields(columns),
      filters: query.filters,
      order: query.order,
    }),
  );
}

const defaultSortBy: Array<SortBy<PagedPersonsQueryField>> = [
  {
    field: "fuldtNavn",
    direction: "asc",
  },
];
